import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import isEqual from 'lodash/isEqual';
import { parse, stringify } from 'lossless-json';

import { Rollout, RolloutDiff, TrackConf } from '@/lib/App';
import { error } from '@/utils/logging';
import { removeLosslessObject } from '@/utils/object';

export interface RolloutContextType {
  // Current rollout state
  rollout: Rollout | null;
  isSaving: boolean;

  // Pending rollout changes
  pendingRollout: Rollout | null;
  pendingApprovals: RolloutDiff[];

  // UI state
  showHistoryPane: boolean;
  showRestorePrompt: boolean;
  startConfigure: boolean;
  isEditRolloutButtonClicked: boolean;
  expandRolloutTargets: boolean;
  enableReorder: boolean;

  // Event tracking
  totalEventCount: number;
  sampleRate: number;

  // Validation state
  isPublished: boolean;
  properlyInstrumentedFacetList: string[];

  // Actions
  setRollout: (rollout: Rollout | null) => void;
  setIsSaving: (saving: boolean) => void;
  setPendingRollout: (rollout: Rollout | null) => void;
  setPendingApprovals: (approvals: RolloutDiff[]) => void;
  setShowHistoryPane: (show: boolean) => void;
  setShowRestorePrompt: (show: boolean) => void;
  setStartConfigure: (start: boolean) => void;
  setIsEditRolloutButtonClicked: (clicked: boolean) => void;
  setExpandRolloutTargets: (expand: boolean) => void;
  setEnableReorder: (enable: boolean) => void;
  setTotalEventCount: (count: number) => void;
  setSampleRate: (rate: number) => void;
  setIsPublished: (published: boolean) => void;
  setProperlyInstrumentedFacetList: (list: string[]) => void;

  // Complex actions
  handleRolloutChange: (rollout: Rollout) => void;
  handleTrackConfChange: (trackConf: TrackConf) => void;
  handleRestore: (rollout: Rollout) => void;
  handleDiscard: (
    latestRollout: Rollout | null,
    defaultRollout: Rollout,
    hasActiveRollout: boolean,
  ) => void;
  handleHistoryToggle: () => void;
  enableStartConfigure: () => void;
  calculateSampleRate: (trackConf: TrackConf) => number;

  // Computed state methods - move these from component
  hasActiveRollout: (latestRollout?: Rollout) => boolean;
  hasPendingChanges: (latestRollout?: Rollout) => boolean;
  handleSaving: (isSaving: boolean, savingSuccess: boolean) => void;
}

const RolloutContext = createContext<RolloutContextType | undefined>(undefined);

export const RolloutContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  // State management
  const [rollout, setRollout] = useState<Rollout | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [pendingRollout, setPendingRollout] = useState<Rollout | null>(null);
  const [pendingApprovals, setPendingApprovals] = useState<RolloutDiff[]>([]);
  const [showHistoryPane, setShowHistoryPane] = useState<boolean>(false);
  const [showRestorePrompt, setShowRestorePrompt] = useState<boolean>(false);
  const [startConfigure, setStartConfigure] = useState<boolean>(false);
  const [isEditRolloutButtonClicked, setIsEditRolloutButtonClicked] =
    useState<boolean>(false);
  const [expandRolloutTargets, setExpandRolloutTargets] =
    useState<boolean>(false);
  const [enableReorder, setEnableReorder] = useState<boolean>(false);
  const [totalEventCount, setTotalEventCount] = useState<number>(0);
  const [sampleRate, setSampleRate] = useState<number>(0);
  const [isPublished, setIsPublished] = useState<boolean>(false);
  const [properlyInstrumentedFacetList, setProperlyInstrumentedFacetList] =
    useState<string[]>([]);

  // Complex action handlers
  const handleRolloutChange = useCallback((newRollout: Rollout) => {
    setRollout(newRollout);
  }, []);

  const handleTrackConfChange = useCallback(
    (trackConf: TrackConf) => {
      if (!rollout) return;

      const updatedRollout = {
        ...rollout,
        trackConf,
      };
      setRollout(updatedRollout);
    },
    [rollout],
  );

  const handleRestore = useCallback((rolloutToRestore: Rollout) => {
    setRollout(rolloutToRestore);
    setShowRestorePrompt(true);
    setStartConfigure(true);
  }, []);

  const handleDiscard = useCallback(
    (
      latestRollout: Rollout | null,
      defaultRollout: Rollout,
      hasActiveRollout: boolean,
    ) => {
      if (hasActiveRollout) {
        setRollout(latestRollout);
      } else {
        setRollout(defaultRollout);
      }
      setShowRestorePrompt(false);
      setStartConfigure(false);
    },
    [],
  );

  const handleHistoryToggle = useCallback(() => {
    setShowHistoryPane((prev) => !prev);
  }, []);

  const enableStartConfigureHandler = useCallback(() => {
    setStartConfigure(true);
  }, []);

  const calculateSampleRate = useCallback((trackConf: TrackConf): number => {
    // Import the round function and TRACK_CONF_MIN_LIMIT from RolloutTrackConf
    const round = (num: number) => Math.round(num * 100) / 100;
    const TRACK_CONF_MIN_LIMIT = 0.99;

    if (!trackConf) return round(1 - TRACK_CONF_MIN_LIMIT);
    const { mode, limit } = trackConf;
    switch (mode) {
      case 2:
        return 0;
      case 1:
        return round(1 - (limit || 0));
      case 0:
        return 1;
      default:
        return round(1 - TRACK_CONF_MIN_LIMIT);
    }
  }, []);

  // Computed state methods
  const hasActiveRollout = useCallback(
    (latestRollout?: Rollout): boolean => {
      return !!latestRollout || !!rollout;
    },
    [rollout],
  );

  const hasPendingChanges = useCallback(
    (latestRollout?: Rollout): boolean => {
      if (!rollout) return false;

      const defaultRollout = latestRollout || null;
      let latestRolloutClone = null;
      if (defaultRollout) {
        latestRolloutClone = parse(stringify(defaultRollout)) as Rollout;
        removeLosslessObject(latestRolloutClone);
      }
      const rolloutClone = parse(stringify(rollout)) as Rollout;
      removeLosslessObject(rolloutClone);

      if (rolloutClone.rollouts) {
        rolloutClone.rollouts.forEach((rolloutTarget) => {
          delete rolloutTarget['reorder'];
        });
      }

      if (pendingRollout && isEditRolloutButtonClicked) {
        return (
          !isEqual(pendingRollout, rolloutClone) &&
          !isEqual(latestRolloutClone, rolloutClone)
        );
      }

      return !isEqual(latestRolloutClone, rolloutClone);
    },
    [rollout, pendingRollout, isEditRolloutButtonClicked],
  );

  const handleSaving = useCallback(
    (savingState: boolean, savingSuccess: boolean) => {
      setIsSaving(savingState);

      if (savingSuccess) {
        window.location.reload();
      }
    },
    [],
  );

  const contextValue = useMemo<RolloutContextType>(
    () => ({
      // State
      rollout,
      isSaving,
      pendingRollout,
      pendingApprovals,
      showHistoryPane,
      showRestorePrompt,
      startConfigure,
      isEditRolloutButtonClicked,
      expandRolloutTargets,
      enableReorder,
      totalEventCount,
      sampleRate,
      isPublished,
      properlyInstrumentedFacetList,

      // Simple setters
      setRollout,
      setIsSaving,
      setPendingRollout,
      setPendingApprovals,
      setShowHistoryPane,
      setShowRestorePrompt,
      setStartConfigure,
      setIsEditRolloutButtonClicked,
      setExpandRolloutTargets,
      setEnableReorder,
      setTotalEventCount,
      setSampleRate,
      setIsPublished,
      setProperlyInstrumentedFacetList,

      // Complex actions
      handleRolloutChange,
      handleTrackConfChange,
      handleRestore,
      handleDiscard,
      handleHistoryToggle,
      enableStartConfigure: enableStartConfigureHandler,
      calculateSampleRate,

      // Computed state methods
      hasActiveRollout,
      hasPendingChanges,
      handleSaving,
    }),
    [
      rollout,
      isSaving,
      pendingRollout,
      pendingApprovals,
      showHistoryPane,
      showRestorePrompt,
      startConfigure,
      isEditRolloutButtonClicked,
      expandRolloutTargets,
      enableReorder,
      totalEventCount,
      sampleRate,
      isPublished,
      properlyInstrumentedFacetList,
      handleRolloutChange,
      handleTrackConfChange,
      handleRestore,
      handleDiscard,
      handleHistoryToggle,
      enableStartConfigureHandler,
      calculateSampleRate,
      hasActiveRollout,
      hasPendingChanges,
      handleSaving,
    ],
  );

  return (
    <RolloutContext.Provider value={contextValue}>
      {children}
    </RolloutContext.Provider>
  );
};

export const useRolloutContext = () => {
  const context = useContext(RolloutContext);
  if (!context) {
    error(
      new Error(
        'useRolloutContext must be used within a RolloutContextProvider',
      ),
    );
  }
  return context;
};
